import { cx } from '@/lib/cx';
import { horarioDosMinutos, MINUTOS_NO_DIA } from '@fiscoai/contracts';

interface ReguaDiaProps {
  /** Minutos do dia (0–1439) em que algo acontece — na captura, as consultas à SEFAZ. */
  horarios: readonly number[];
  /** Faixa hachurada depois de cada marca (a espera mínima da SEFAZ), em minutos. */
  esperaMinutos?: number;
  /** Leitura em texto da régua: é o que o leitor de tela anuncia (o desenho é decorativo). */
  legenda: string;
  /** Minuto do dia de agora: uma linha fina mostra onde o dia está. */
  agoraMinutos?: number;
  className?: string;
}

const HORAS_ROTULADAS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
/** Com mais marcas que isto, os horários sobre os pontos viram ruído: fica só a legenda. */
const MAXIMO_ROTULOS = 6;
/** Dois rótulos a menos disto se sobrepõem: o segundo desce uma linha. */
const PROXIMO_DEMAIS_MIN = 150;

const pct = (minutos: number) => `${String((minutos / MINUTOS_NO_DIA) * 100)}%`;

function Espera({ inicio, minutos }: { inicio: number; minutos: number }) {
  const fim = inicio + minutos;
  const trechos =
    fim <= MINUTOS_NO_DIA
      ? [[inicio, minutos]]
      : [
          [inicio, MINUTOS_NO_DIA - inicio],
          [0, fim - MINUTOS_NO_DIA],
        ];
  return trechos.map(([de = 0, duracao = 0]) => (
    <span
      key={de}
      className="absolute inset-y-1 rounded-mini bg-[repeating-linear-gradient(135deg,var(--regua-espera)_0_3px,transparent_3px_6px)] transition-[left,width] duration-[var(--duracao-media)] ease-[var(--ease-saida)] motion-reduce:transition-none"
      style={{ left: pct(de), width: pct(duracao) }}
    />
  ));
}

function Rotulos({ horarios }: { horarios: readonly number[] }) {
  if (horarios.length > MAXIMO_ROTULOS) return <div className="h-2" />;
  return (
    <div className="relative h-9">
      {horarios.map((m, i) => {
        const desce = i % 2 === 1 && m - (horarios[i - 1] ?? -Infinity) < PROXIMO_DEMAIS_MIN;
        return (
          <span
            key={m}
            className={cx(
              'absolute -translate-x-1/2 text-xs font-semibold tabular text-marca transition-[left] duration-[var(--duracao-media)] ease-[var(--ease-saida)] motion-reduce:transition-none',
              desce ? 'top-4' : 'top-0',
            )}
            style={{ left: pct(m) }}
          >
            {horarioDosMinutos(m)}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Um dia de 24 h na escala do relógio, com as marcas do que está agendado e a espera depois de
 * cada uma. Os pontos deslizam quando a agenda muda (sem movimento com `prefers-reduced-motion`).
 */
export function ReguaDia({
  horarios,
  esperaMinutos = 0,
  legenda,
  agoraMinutos,
  className,
}: ReguaDiaProps) {
  const ordenados = [...horarios].sort((a, b) => a - b);
  return (
    <figure className={cx('grid gap-1', className)}>
      <div aria-hidden className="grid">
        <Rotulos horarios={ordenados} />
        <div className="relative h-7 rounded-controle border border-borda bg-superficie-afundada">
          {Array.from({ length: 23 }, (_, i) => i + 1).map((h) => (
            <span
              key={h}
              className={cx('absolute bottom-0 w-px bg-borda-forte', h % 6 === 0 ? 'h-3' : 'h-1.5')}
              style={{ left: pct(h * 60) }}
            />
          ))}
          {esperaMinutos > 0 &&
            ordenados.map((m) => <Espera key={m} inicio={m} minutos={esperaMinutos} />)}
          {agoraMinutos !== undefined && (
            <span
              className="absolute -inset-y-1 w-0.5 -translate-x-1/2 rounded-pilula bg-texto-suave"
              style={{ left: pct(agoraMinutos) }}
            />
          )}
          {ordenados.map((m) => (
            <span
              key={m}
              className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-pilula bg-marca ring-2 ring-superficie transition-[left] duration-[var(--duracao-media)] ease-[var(--ease-saida)] motion-reduce:transition-none"
              style={{ left: pct(m) }}
            />
          ))}
        </div>
        <div className="relative h-4 text-mini tabular text-texto-fraco">
          {HORAS_ROTULADAS.map((h) => (
            <span
              key={h}
              className={cx(
                'absolute top-0.5',
                h === 0 ? '' : h === 24 ? '-translate-x-full' : '-translate-x-1/2',
              )}
              style={{ left: pct(h * 60) }}
            >
              {String(h).padStart(2, '0')}
            </span>
          ))}
        </div>
      </div>
      <figcaption className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
        <span>{legenda}</span>
        <span aria-hidden className="flex items-center gap-3 text-xs text-texto-fraco">
          <span className="flex items-center gap-1.5">
            <span className="size-2 rounded-pilula bg-marca" /> consulta
          </span>
          {esperaMinutos > 0 && (
            <span className="flex items-center gap-1.5">
              <span className="h-2 w-4 rounded-mini bg-[repeating-linear-gradient(135deg,var(--regua-espera)_0_3px,transparent_3px_6px)]" />
              espera da SEFAZ
            </span>
          )}
          {agoraMinutos !== undefined && (
            <span className="flex items-center gap-1.5">
              <span className="h-3 w-0.5 rounded-pilula bg-texto-suave" /> agora
            </span>
          )}
        </span>
      </figcaption>
    </figure>
  );
}
